import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import App from './App'
import { hasAttemptedChunkReload, markChunkReloadAttempted } from './utils/chunkReload'
// Self-hosted variable fonts (fontsource) — no Google Fonts CDN request,
// same GDPR posture as the consent-gated analytics. latin + latin-ext
// subsets load via unicode-range, so Romanian diacritics are covered.
import '@fontsource-variable/inter'
// True italics for the `italic` utility already used on body text
// (notes, quotes, placeholders) — without this face the browser
// synthesizes an oblique slant from upright Inter.
import '@fontsource-variable/inter/wght-italic.css'
import '@fontsource-variable/bricolage-grotesque'
import '@fontsource-variable/fraunces/wght-italic.css'
import './index.css'
// Preload the display face (Bricolage drives every public-page H1, the
// LCP element on marketing pages) so the woff2 fetch starts with the
// first network waterfall instead of after the CSS chunk resolves —
// without it, cold-cache visitors see the hero reflow on font swap.
// ?url makes Vite resolve the hashed asset path; latin-ext rides along
// because Romanian diacritics live there. crossOrigin is mandatory on
// font preloads even same-origin, or the browser double-fetches.
import bricolageLatinUrl from '@fontsource-variable/bricolage-grotesque/files/bricolage-grotesque-latin-wght-normal.woff2?url'
import bricolageLatinExtUrl from '@fontsource-variable/bricolage-grotesque/files/bricolage-grotesque-latin-ext-wght-normal.woff2?url'

for (const href of [bricolageLatinUrl, bricolageLatinExtUrl]) {
  // This module runs once in prod, but Vite HMR re-evaluates it on every
  // edit — skip hrefs already preloaded so dev doesn't stack duplicate links.
  if (document.head.querySelector(`link[rel="preload"][href="${href}"]`)) continue
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'font'
  link.type = 'font/woff2'
  link.crossOrigin = 'anonymous'
  link.href = href
  document.head.appendChild(link)
}

// Stale-chunk recovery, browser-agnostic. After a new Vercel deploy, an old
// client's lazy import() targets a hashed filename that no longer exists →
// Vercel's SPA rewrite serves index.html for the missing /assets/*.js, and the
// browser throws while parsing it (Chrome: "Failed to fetch dynamically
// imported module"; Safari: "'text/html' is not a valid JavaScript MIME
// type"). Vite fires `vite:preloadError` for exactly this failure regardless of
// the browser's error text, and it surfaces the cases that reach window.onerror
// instead of React's render tree (where ErrorBoundary would catch them). Reload
// once to pull the fresh index.html + new chunk hashes. The same sessionStorage
// flag ErrorBoundary uses keeps the two from double-reloading and stops a loop
// if the reload itself keeps failing; it clears on tab close.
window.addEventListener('vite:preloadError', (event) => {
  // hasAttemptedChunkReload / markChunkReloadAttempted guard every
  // sessionStorage access and fall back to window.name where storage is
  // disabled (see utils/chunkReload). Shared with ErrorBoundary so the two
  // recovery paths can't double-reload or drift.
  //
  // Already reloaded once and still failing → let Vite re-throw so the error
  // reaches the ErrorBoundary / Sentry. A genuinely broken deploy should
  // surface, not be swallowed into a silent non-recovery.
  if (hasAttemptedChunkReload()) return
  // We ARE going to recover: cancel Vite's default re-throw so this handled
  // chunk error doesn't propagate to window.onerror and pollute Sentry.
  event.preventDefault()
  markChunkReloadAttempted()
  window.location.reload()
})

// Sentry is no longer initialised here — it would start session replay
// and the browser-tracing fetch wrapper before the user has accepted
// the "Analize" cookie category, which fails Cookiebot scans. The init
// now lives in ConsentContext, gated on consent.analytics, via
// utils/analytics/sentry.js.

// 5-minute staleTime: navigating between salon tabs within 5 min reuses
// cached data instantly. Longer-lived data (salon identity, subscription)
// overrides this per-query.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Tag the root element when running inside the Capacitor native shell so CSS
// can opt into app-only behaviour (native-feel resets in index.css, the
// directional page slide) without touching the web build, which ships the
// same bundle.
if (Capacitor.isNativePlatform()) {
  document.documentElement.classList.add('native');
  // Disable pinch / double-tap zoom in the native shell — zooming the whole UI
  // is a "this is a web page" tell (App Store 4.2) and leaves shrunk content
  // with blank margins. WKWebView ignores `user-scalable=no` but DOES honour
  // min/max-scale, and it derives the scrollView zoom limits from the viewport
  // AFTER the native capacitorDidLoad zoom-lock runs (overriding it) — so the cap
  // must live in the viewport itself. maximum-scale=1 blocks zoom-IN; minimum-scale=1
  // blocks zoom-OUT (pinch-together) — and zoom-OUT is exactly what shrinks the page
  // into the blank-margin state described above, so without it the cap was only
  // half-done. Android's WebView honours user-scalable=no. Native-only: the website
  // keeps pinch-zoom for accessibility.
  const vp = document.querySelector('meta[name="viewport"]');
  if (vp) {
    vp.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
    );
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
)
